import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { message, notification } from 'antd';
import { refreshToken } from '@/services/common/api';
import { redirectToSSO } from '@/utils/sso';

// 不需要登录的页面路由
const noLoginPath = ['/questionnaire'];

// 获取页面路由
const hashPath = window.location.hash;
const pathname = hashPath.replace('#', '').split('?')[0];

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}

// 与后端约定的响应数据格式
interface ResponseStructure {
  code: number;
  message: string;
  requestToken: string;
  resultCode: string;
  resultObject: any;
  status?: string;
  traceId?: string;
  msg?: string;
}

/**
 * 清除 token 并跳转到 SSO 登录
 */
function clearTokensAndRedirectToSSO(): void {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('userCode');
  message.error('登录已失效，请重新登录');
  redirectToSSO();
}

/**
 * 处理 401 错误：尝试刷新 token
 */
async function handle401Error(options: RequestOptions): Promise<any> {
  const refreshTokenValue = localStorage.getItem('refreshToken');
  const userCode = localStorage.getItem('userCode') || undefined;

  // 如果没有 refreshToken 或 userCode，直接跳转到 SSO 登录
  if (!refreshTokenValue || !userCode) {
    clearTokensAndRedirectToSSO();
    return Promise.reject(new Error('No refresh token'));
  }

  // 等待 refreshToken 请求完成
  let refreshResponse;
  try {
    refreshResponse = await refreshToken(userCode, refreshTokenValue);
  } catch (error) {
    // refreshToken 请求失败，跳转到 SSO 登录
    clearTokensAndRedirectToSSO();
    return Promise.reject(error);
  }

  // refreshToken 请求完成，检查结果
  const newAccessToken = refreshResponse?.resultObject;

  if (newAccessToken) {
    // 刷新成功，更新 accessToken，重试原请求
    localStorage.setItem('accessToken', newAccessToken);

    const { request } = await import('@umijs/max');
    // @ts-ignore
    const originalUrl = options.originalUrl || options.url;


    // 构建重试请求配置，保持原请求的所有配置包括 headers
    const retryConfig: any = {
      method: options.method || 'GET',
    };

    // 添加请求体（POST/PUT等）
    if (options.data !== undefined) {
      retryConfig.data = options.data;
    }

    // 添加查询参数（GET等）
    if (options.params !== undefined) {
      retryConfig.params = options.params;
    }

    // 保持原请求的 headers，特别是 Content-Type
    // @ts-ignore
    if (options.headers) {
      options.headers.Authorization = `Bearer ${newAccessToken}`;
      retryConfig.headers = {
        // @ts-ignore
        ...options.headers,
      };
    }

    // 重试请求
    // request 方法已经通过 formatResult 处理，返回的是 resultObject
    // 但响应拦截器需要返回响应对象格式，所以需要包装一下
    const retryResponse = await request(originalUrl, retryConfig);
    
    // 返回响应对象格式，确保数据能正确传递
    return {
      data: retryResponse,
      status: 200,
      statusText: 'OK',
      headers: {},
      config: retryConfig,
    };

  } else {
    // 刷新失败，跳转到 SSO 登录
    clearTokensAndRedirectToSSO();
    return Promise.reject(new Error('Token refresh failed'));
  }
}

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      // 根据实际响应结构处理错误
    },
    // 错误接收及处理
    errorHandler: async (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;
      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: any = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode, showType } = errorInfo;
          switch (showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errorMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              message.error(errorMessage);
          }
        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        message.error(`Response status:${error.response.status}`);
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        message.error('None response! Please retry.');
      } else {
        // 发送请求时出了点问题
        message.error('Request error, please retry.');
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      // 保存原始 URL，用于响应拦截器重试
      // @ts-ignore
      if (!config.originalUrl) {
        // @ts-ignore
        config.originalUrl = config.url;
      }

      // 处理 URL：完整 URL 直接使用，相对路径添加 /titan 前缀
      // 注意：登录服务已在 loginServer.ts 中处理为完整 URL 或 /devops-login 前缀，不需要在这里处理
      if (config.url && !config.url.startsWith('http://') && !config.url.startsWith('https://')) {
        config.url = '/titan' + config.url;
      }

      // 添加 accessToken
      const accessToken = localStorage.getItem('accessToken');
      if (accessToken) {
        // @ts-ignore
        config.headers.common.Authorization = `Bearer ${accessToken}`;
      }

      return config;
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    // @ts-ignore - umi response interceptor type issue
    async (response: any, options: any) => {
      const data = response.data || {};
      const { code, status, msg } = data as ResponseStructure;
    
      if ((code === 401 || status === '401') && !(noLoginPath.indexOf(pathname) > -1)) {
        return handle401Error(response?.config);
      }

      // 处理其他错误
      if (status === 'false') {
        message.error('请求失败！');
      }

      if ((code === 500 || status === '500')) {
        if (msg) {
          message.error(msg);
        }else {
          message.error('服务器错误');
        }
      }

      return response || {};
    },
  ],
};
